// ========================================
// CONTENT RESOLVER — Unified Content Lookup
// ========================================
// [PURPOSE]: Single source of truth for resolving any content (races, classes, subclasses)
// [WHY]: Replaces 15+ scattered SRD-only lookups with unified SRD + custom source resolution
// [INTEGRATION]: Used by effectCalculator, spellCalculations, useRaceEffects, useClassEffects,
//   MulticlassManager, App.jsx, CharacterBasicsPanel, and all consumers of class/race data
//
// CORE PRINCIPLE: Every lookup returns an OBJECT or null. Never a string. No exceptions.
// Custom content produces the exact same object shapes as SRD content.

import { SRD_CLASSES, SRD_RACES, SRD_SUBCLASSES } from '../data/srdCharacterData';
import { sourcesManager } from '../data/sourcesManager';
import { lookupSparseTable } from './sparseTableLookup';

// ========================================
// CLASS RESOLUTION
// ========================================

/**
 * Resolve a class by name, checking SRD first then custom sources.
 * Always returns a full class object or null — never a string.
 *
 * @param {string} className - The class name to look up (case-sensitive)
 * @returns {Object|null} Full class data object with hitDice, features, spellcasting, etc.
 */
export function getClassData(className) {
  if (!className) return null;

  // [PRIORITY 1]: Check SRD classes (always available, no source toggle needed)
  if (SRD_CLASSES[className]) {
    return SRD_CLASSES[className];
  }

  // [PRIORITY 2]: Check custom sources (from Content Workshop or imported sources)
  const customClasses = sourcesManager.getAllClasses();
  if (customClasses[className]) {
    return customClasses[className];
  }

  // [PRIORITY 3]: Case-insensitive fallback for robustness
  const classNameLower = className.toLowerCase();
  for (const [key, value] of Object.entries(SRD_CLASSES)) {
    if (key.toLowerCase() === classNameLower) return value;
  }
  for (const [key, value] of Object.entries(customClasses)) {
    if (key.toLowerCase() === classNameLower) return value;
  }

  // [PRIORITY 4 — CW-TRIO-SOURCES 2026-07-12]: Match the embedded display
  // name. Source content is keyed by a normalized slug (addClassToSource:
  // name.toLowerCase().replace(/[^a-z0-9]/g,'_')), so any multi-word class
  // name ('Blood Hunter' → key 'blood_hunter') never key-matches its own
  // display name and resolved null here. getSubclassData already matches
  // .name this way (see the subclass.name check below).
  for (const value of Object.values(customClasses)) {
    if (value && typeof value.name === 'string' && value.name.toLowerCase() === classNameLower) {
      return value;
    }
  }

  return null;
}

/**
 * Get all available classes (SRD + all enabled custom sources).
 * Returns a merged object keyed by class name.
 *
 * @returns {Object} Merged class data: { 'Wizard': {...}, 'Artificer': {...}, 'MyCustomClass': {...} }
 */
export function getAllClasses() {
  return { ...SRD_CLASSES, ...sourcesManager.getAllClasses() };
}

// ========================================
// RACE RESOLUTION
// ========================================

/**
 * Resolve a race by name, checking SRD first then custom sources.
 * Handles subrace merging when subraceName is provided.
 * Always returns a full race object or null — never a string.
 *
 * @param {string} raceName - The race name to look up
 * @param {string|null} subraceName - Optional subrace name to merge
 * @returns {Object|null} Full race data with abilityScoreIncrease, features, speed, etc.
 */
export function getRaceData(raceName, subraceName = null) {
  if (!raceName) return null;

  let raceData = null;

  // [PRIORITY 1]: Check SRD races
  if (SRD_RACES[raceName]) {
    raceData = { ...SRD_RACES[raceName] };
  }

  // [PRIORITY 2]: Check custom sources
  if (!raceData) {
    const customRaces = sourcesManager.getAllRaces();
    if (customRaces[raceName]) {
      raceData = { ...customRaces[raceName] };
    }
  }

  // [PRIORITY 3]: Case-insensitive fallback
  if (!raceData) {
    const raceNameLower = raceName.toLowerCase();
    for (const [key, value] of Object.entries(SRD_RACES)) {
      if (key.toLowerCase() === raceNameLower) {
        raceData = { ...value };
        break;
      }
    }
    if (!raceData) {
      const customRaces = sourcesManager.getAllRaces();
      for (const [key, value] of Object.entries(customRaces)) {
        if (key.toLowerCase() === raceNameLower) {
          raceData = { ...value };
          break;
        }
      }
      // [PRIORITY 4 — CW-TRIO-SOURCES 2026-07-12]: Match the embedded display
      // name. Source content is keyed by a normalized slug (addRaceToSource:
      // name.toLowerCase().replace(/[^a-z0-9]/g,'_')), so any multi-word race
      // name ('Half-Ogre' → key 'half_ogre') never key-matches its own display
      // name and resolved null here — gameplay-breaking for spaced names.
      if (!raceData) {
        for (const value of Object.values(customRaces)) {
          if (value && typeof value.name === 'string' && value.name.toLowerCase() === raceNameLower) {
            raceData = { ...value };
            break;
          }
        }
      }
    }
  }

  if (!raceData) return null;

  // [SUBRACE MERGE]: Apply subrace overrides if provided
  if (subraceName && raceData.subraces && raceData.subraces[subraceName]) {
    const subrace = raceData.subraces[subraceName];

    if (subrace.abilityScoreIncrease) {
      raceData.abilityScoreIncrease = {
        ...raceData.abilityScoreIncrease,
        ...subrace.abilityScoreIncrease
      };
    }
    if (subrace.features) {
      raceData.features = [...(raceData.features || []), ...subrace.features];
    }
    if (subrace.proficiencies) {
      raceData.proficiencies = {
        ...(raceData.proficiencies || {}),
        ...subrace.proficiencies
      };
    }
    if (subrace.speed) raceData.speed = subrace.speed;
    if (subrace.darkvision) raceData.darkvision = subrace.darkvision;
    if (subrace.languages) {
      raceData.languages = [...(raceData.languages || []), ...subrace.languages];
    }
    if (subrace.hitPointBonus) raceData.hitPointBonus = subrace.hitPointBonus;
    if (subrace.savingThrows) {
      raceData.savingThrows = {
        ...(raceData.savingThrows || {}),
        ...subrace.savingThrows
      };
    }
    // [CUSTOM RACE FIELDS]: Apply additional custom properties from subrace
    if (subrace.damageResistance) raceData.damageResistance = subrace.damageResistance;
    if (subrace.effects) raceData.effects = [...(raceData.effects || []), ...subrace.effects];
    if (subrace.grantedSpells) raceData.grantedSpells = subrace.grantedSpells;
    if (subrace.featureDescription) raceData.subraceFeatureDescription = subrace.featureDescription;
    // [v1.0]: Also pass through plural featureDescriptions (object keyed by feature name)
    if (subrace.featureDescriptions) raceData.subraceFeatureDescriptions = subrace.featureDescriptions;
  }

  return raceData;
}

/**
 * Get all available races (SRD + all enabled custom sources).
 *
 * @returns {Object} Merged race data keyed by race name
 */
export function getAllRaces() {
  return { ...SRD_RACES, ...sourcesManager.getAllRaces() };
}

/**
 * Get available subraces for a race.
 *
 * @param {string} raceName - The race name
 * @returns {string[]} Array of subrace names, or empty array
 */
export function getSubraces(raceName) {
  const raceData = getRaceData(raceName);
  if (!raceData || !raceData.subraces) return [];
  return Object.keys(raceData.subraces);
}

// ========================================
// SUBCLASS RESOLUTION
// ========================================

/**
 * Resolve a subclass by class name and subclass name.
 * Checks class-embedded subclasses first, then SRD_SUBCLASSES, then custom sources.
 *
 * @param {string} className - The parent class name
 * @param {string} subclassName - The subclass name
 * @returns {Object|null} Subclass data object
 */
export function getSubclassData(className, subclassName) {
  if (!className || !subclassName) return null;

  // [PRIORITY 1]: Check embedded subclasses in class definition
  const classData = getClassData(className);
  if (classData?.subclasses?.[subclassName]) {
    return classData.subclasses[subclassName];
  }

  // [PRIORITY 2]: Check SRD_SUBCLASSES (separate export)
  if (SRD_SUBCLASSES?.[className]?.[subclassName]) {
    return SRD_SUBCLASSES[className][subclassName];
  }

  // [PRIORITY 3]: Check custom sources
  const customSubclasses = sourcesManager.getSubclassesForClass(className);
  for (const [key, subclass] of Object.entries(customSubclasses)) {
    if (subclass.name === subclassName || key === subclassName) {
      return subclass;
    }
  }

  return null;
}

/**
 * Get all subclasses for a given class (embedded + SRD_SUBCLASSES + custom sources).
 *
 * @param {string} className - The class name
 * @returns {Object} All subclasses keyed by name
 */
export function getSubclassesForClass(className) {
  if (!className) return {};

  const classData = getClassData(className);
  const embedded = classData?.subclasses || {};
  const srdSeparate = SRD_SUBCLASSES?.[className] || {};
  const custom = sourcesManager.getSubclassesForClass(className);

  return { ...embedded, ...srdSeparate, ...custom };
}

// ========================================
// CASTER TYPE RESOLUTION
// ========================================

// [PURPOSE]: Normalize the caster type string from different formats
// Custom classes use 'Full caster', 'Half caster', etc. (from spellSlots field)
// SRD classes now use 'full', 'half', 'pact' (from casterType field)
const CASTER_TYPE_MAP = {
  'full': 'full',
  'half': 'half',
  'third': 'third',
  'pact': 'pact',
  'Full caster': 'full',
  'Half caster': 'half',
  'Third caster': 'third',
  'Pact magic': 'pact',
  'One-third caster progression': 'third',
  'None': null,
};

/**
 * Get the caster type for a class by reading its spellcasting properties.
 * NO hardcoded class name arrays — reads directly from the class object.
 *
 * @param {string} className - The class name
 * @returns {'full'|'half'|'third'|'pact'|null} Normalized caster type
 */
export function getCasterType(className) {
  const classData = getClassData(className);

  // [CHECK 0]: If getClassData returned null, try subclass fallback
  // This handles cases like getCasterType('eldritch knight') where the name
  // is a subclass, not a base class. Search all classes' subclasses for a match.
  if (!classData) {
    if (!className) return null;
    const nameLower = className.toLowerCase();

    // Search SRD classes for a matching subclass
    for (const classObj of Object.values(SRD_CLASSES)) {
      if (!classObj.subclasses) continue;
      for (const [subName, subData] of Object.entries(classObj.subclasses)) {
        if (subName.toLowerCase() === nameLower) {
          // Found the subclass — check its spellcasting properties
          if (subData.spellcasting?.casterType) {
            return CASTER_TYPE_MAP[subData.spellcasting.casterType] ?? null;
          }
          if (subData.spellcasting?.spellSlots) {
            return CASTER_TYPE_MAP[subData.spellcasting.spellSlots] ?? null;
          }
          // Subclass exists but has no spellcasting — not a caster subclass
          return null;
        }
      }
    }

    // Also search custom sources
    const customClasses = sourcesManager.getAllClasses();
    for (const classObj of Object.values(customClasses)) {
      if (!classObj.subclasses) continue;
      for (const [subName, subData] of Object.entries(classObj.subclasses)) {
        if (subName.toLowerCase() === nameLower) {
          if (subData.spellcasting?.casterType) {
            return CASTER_TYPE_MAP[subData.spellcasting.casterType] ?? null;
          }
          if (subData.spellcasting?.spellSlots) {
            return CASTER_TYPE_MAP[subData.spellcasting.spellSlots] ?? null;
          }
          return null;
        }
      }
    }

    return null;
  }

  // [CHECK 1]: Explicit casterType field (SRD format, added in v1.0)
  if (classData.spellcasting?.casterType) {
    return CASTER_TYPE_MAP[classData.spellcasting.casterType] ?? null;
  }

  // [CHECK 2]: spellSlots string (custom class format from Content Workshop)
  if (classData.spellcasting?.spellSlots) {
    return CASTER_TYPE_MAP[classData.spellcasting.spellSlots] ?? null;
  }

  // [CHECK 3]: Legacy pactMagic boolean (older data format)
  if (classData.spellcasting?.pactMagic) {
    return 'pact';
  }

  // [CHECK 4]: Has spellcasting config but no type indicator
  if (classData.spellcasting && !classData.spellcasting.casterType && !classData.spellcasting.spellSlots) {
    return null;
  }

  return null;
}

/**
 * Check if a class is any kind of spellcaster.
 *
 * @param {string} className - The class name
 * @returns {boolean} True if the class has any spellcasting ability
 */
export function isSpellcaster(className) {
  return getCasterType(className) !== null;
}

/**
 * Check if a class uses prepared spells (Wizard, Cleric, Druid, Paladin, Artificer).
 * Reads from class data — no hardcoded name arrays.
 *
 * @param {string} className - The class name
 * @returns {boolean} True if the class prepares spells from a list
 */
export function isPreparedCaster(className) {
  const classData = getClassData(className);
  if (!classData?.spellcasting) return false;

  // Check explicit preparedSpells property (SRD format: string descriptor)
  if (classData.spellcasting.preparedSpells) return true;

  // Check custom format (boolean)
  if (classData.spellcasting.preparedSpells === true) return true;

  // Wizard uses a spellbook (special case of prepared)
  if (classData.spellcasting.spellbook) return true;

  return false;
}

/**
 * Check if a class uses a spellbook (currently only Wizard).
 * Reads from class data — no hardcoded name check.
 *
 * @param {string} className - The class name
 * @returns {boolean}
 */
export function usesSpellbook(className) {
  const classData = getClassData(className);
  return classData?.spellcasting?.spellbook === true;
}

/**
 * Get the spellcasting ability for a class.
 *
 * @param {string} className - The class name
 * @returns {string|null} Ability abbreviation ('int', 'wis', 'cha') or null
 */
export function getSpellcastingAbility(className) {
  const classData = getClassData(className);
  return classData?.spellcasting?.ability || null;
}

/**
 * Get the level at which a class starts spellcasting.
 * Full casters start at 1, half casters at 2, etc.
 *
 * @param {string} className - The class name
 * @returns {number} Starting level for spellcasting (defaults to 1)
 */
export function getSpellcastingStartLevel(className) {
  const classData = getClassData(className);
  if (!classData?.spellcasting) return Infinity;
  return classData.spellcasting.startLevel || 1;
}

/**
 * Get hit dice for a class (e.g., '1d8', '1d10', '1d12').
 *
 * @param {string} className - The class name
 * @returns {string|null} Hit dice string or null
 */
export function getHitDice(className) {
  const classData = getClassData(className);
  return classData?.hitDice || null;
}

/**
 * Get multiclass requirements for a class.
 * SRD classes may have these in a separate structure; custom classes store in multiclassRequirements.
 *
 * @param {string} className - The class name
 * @returns {Object|null} Requirements object keyed by ability score, e.g. { intelligence: 13 }
 */
export function getMulticlassRequirements(className) {
  const classData = getClassData(className);
  return classData?.multiclassRequirements || null;
}

/**
 * [v1.0]: Check if a class gains a Fighting Style feature.
 * Reads from class features — no hardcoded ['Fighter', 'Paladin', 'Ranger'] arrays.
 *
 * @param {string} className - The class name
 * @returns {boolean} True if the class has Fighting Style in early level features
 */
export function hasFightingStyle(className) {
  const classData = getClassData(className);
  if (!classData?.features) return false;

  // Check levels 1-2 features for 'Fighting Style'
  for (let level = 1; level <= 2; level++) {
    const levelFeatures = classData.features[level];
    if (Array.isArray(levelFeatures) && levelFeatures.some(f =>
      (typeof f === 'string' ? f : f.name) === 'Fighting Style'
    )) {
      return true;
    }
  }
  return false;
}

/**
 * [v1.0]: Check if a class uses the "spells known" mechanic.
 * These are spellcasters that learn a fixed number of spells (not prepared, not spellbook).
 * Examples: Bard, Sorcerer, Warlock, Ranger.
 * Reads from class spellcasting.spellsKnown property.
 *
 * @param {string} className - The class name
 * @returns {boolean} True if the class uses spells known
 */
export function usesSpellsKnown(className) {
  const classData = getClassData(className);
  if (!classData?.spellcasting) return false;
  // Has spellsKnown table AND is not a prepared caster (Wizard prepares but also has spellbook)
  return !!classData.spellcasting.spellsKnown && !isPreparedCaster(className);
}

/**
 * [v1.0]: Get the number of spells known at a given level for a class.
 *
 * @param {string} className - The class name
 * @param {number} level - Character level
 * @returns {number|null} Number of spells known at that level, or null
 */
export function getSpellsKnownAtLevel(className, level) {
  const classData = getClassData(className);
  if (!classData?.spellcasting?.spellsKnown) return null;
  // [Q15 BACKPORT 2026-05-16]: spellsKnown tables are SPARSE — only level-change
  //   rows are declared. lookupSparseTable returns the value at the highest
  //   declared level ≤ the target. Returning null (not undefined) preserves
  //   the existing API contract for consumers of this function.
  const result = lookupSparseTable(classData.spellcasting.spellsKnown, level);
  return result === undefined ? null : result;
}

// ========================================
// DRAGONBORN BREATH WEAPON RESOLUTION (P2.11 — Apr 2026)
// ========================================
// [PURPOSE]: Read breath weapon details (damage type + shape/save) from a
//   character's race + subrace, resolved through getRaceData so SRD AND
//   any custom dragonborn-shaped homebrew (with the same {damageType,
//   breathWeapon} shape on subrace data) work identically.
// [WHY]: Other races' once-per-rest powers have static descriptors in
//   raceResources.js (Half-Orc Relentless Endurance, Tiefling Hellish
//   Rebuke, etc.). Dragonborn is the one race whose details vary per
//   ancestry (10 subraces × 2 axes), so the runtime resolves at-use-time.
// [INTEGRATION]: Helpers are seeds for a v1.x breath-weapon UI consumer
//   (no current consumer wires a "Use Breath Weapon" button — see
//   raceResources.js Dragonborn entry which only registers the resource
//   counter). Without these helpers a future UI would have to repeat the
//   subrace-lookup boilerplate; centralising here.
// [DESIGN]: NO hardcoded ancestry → element table. Every value resolves
//   through getRaceData(raceName, subraceName) so custom homebrew
//   dragonborn ancestries (declared via Content Workshop) automatically
//   work without code changes.

/**
 * Get the breath weapon damage type for a Dragonborn character.
 *
 * Returns the damageType string (e.g. 'Acid', 'Fire', 'Cold') from the
 * character's selected ancestry subrace. Returns null for non-Dragonborn
 * characters or Dragonborn without an ancestry chosen.
 *
 * @param {Object} character - Character object with basics.race + basics.subrace
 * @returns {string|null} Damage type string from SRD data, or null
 */
export function getBreathWeaponDamageType(character) {
  if (!character?.basics) return null;
  const { race, subrace } = character.basics;
  if (!race || !subrace) return null;
  const raceData = getRaceData(race);
  if (!raceData?.subraces) return null;
  const subraceData = raceData.subraces[subrace];
  if (!subraceData) return null;
  return subraceData.damageType || null;
}

/**
 * Get the breath weapon shape (line/cone) and save type for a Dragonborn
 * character.
 *
 * Returns the breathWeapon descriptor string (e.g. '5 by 30 ft. line
 * (Dex. save)') from the character's selected ancestry subrace. Returns
 * null for non-Dragonborn characters or Dragonborn without ancestry.
 *
 * @param {Object} character - Character object with basics.race + basics.subrace
 * @returns {string|null} Breath shape descriptor from SRD data, or null
 */
export function getBreathWeaponShape(character) {
  if (!character?.basics) return null;
  const { race, subrace } = character.basics;
  if (!race || !subrace) return null;
  const raceData = getRaceData(race);
  if (!raceData?.subraces) return null;
  const subraceData = raceData.subraces[subrace];
  if (!subraceData) return null;
  return subraceData.breathWeapon || null;
}

// ========================================
// CONVENIENCE EXPORTS
// ========================================

// [PURPOSE]: Default export as a namespace for consumers who prefer contentResolver.getClassData()
const contentResolver = {
  getClassData,
  getAllClasses,
  getRaceData,
  getAllRaces,
  getSubraces,
  getSubclassData,
  getSubclassesForClass,
  getCasterType,
  isSpellcaster,
  isPreparedCaster,
  usesSpellbook,
  usesSpellsKnown,
  getSpellsKnownAtLevel,
  hasFightingStyle,
  getSpellcastingAbility,
  getSpellcastingStartLevel,
  getHitDice,
  getMulticlassRequirements,
  getBreathWeaponDamageType,
  getBreathWeaponShape,
};

export default contentResolver;
